import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import GUI from 'lil-gui'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import holographicVertexShader from './shaders/holographic/vertex.glsl'
import holographicFragmentShader from './shaders/holographic/fragment.glsl'

/**
 * Loader
 */
const loadingOverlay = document.getElementById('loading-overlay')
const loadingPercent = document.getElementById('loading-percent')

const updateLoadingProgress = (progress) => {
    const displayProgress = Math.min(Math.floor(progress), 99)
    loadingPercent.textContent = `${displayProgress}%`
}

const finishLoading = () => {
    loadingPercent.textContent = '99%'

    setTimeout(() => {
        loadingPercent.textContent = '100%'

        setTimeout(() => {
            loadingOverlay.classList.add('loaded')
            gui.domElement.style.display = 'block'
        }, 200)
    }, 1000)
}

/**
 * Base
 */
// Debug
const gui = new GUI()
gui.domElement.style.display = 'none'

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

// Loaders
const gltfLoader = new GLTFLoader()

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Mouse for parallax
 */
const mouse = {
    x: 0,
    y: 0,
    targetX: 0,
    targetY: 0
}

window.addEventListener('mousemove', (event) => {
    mouse.targetX = (event.clientX / sizes.width - 0.5) * 2
    mouse.targetY = (event.clientY / sizes.height - 0.5) * 2
})

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(25, sizes.width / sizes.height, 0.1, 100)
camera.position.set(7, 7, 7)
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.dampingFactor = 0.05

// Z (polar angle) limits
controls.minPolarAngle = Math.PI * 0.25
controls.maxPolarAngle = Math.PI * 0.75

// Disable pan
controls.enablePan = false

// Zoom limits
controls.minDistance = 5
controls.maxDistance = 20

/**
 * Renderer
 */
const rendererParameters = {}
rendererParameters.clearColor = '#1d1f2a'

const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true
})
renderer.setClearColor(rendererParameters.clearColor)
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

gui
    .addColor(rendererParameters, 'clearColor')
    .onChange(() => {
        renderer.setClearColor(rendererParameters.clearColor)
    })

/**
 * Material
 */
const materialParametrs = {}
materialParametrs.color = '#b8c9f5'

gui
    .addColor(materialParametrs, 'color')
    .onChange(() => {
        material.uniforms.uColor.value.set(materialParametrs.color)
    })

const material = new THREE.ShaderMaterial({
    vertexShader: holographicVertexShader,
    fragmentShader: holographicFragmentShader,
    transparent: true,
    uniforms: {
        uTime: { value: 0 },
        uColor: new THREE.Uniform(new THREE.Color(materialParametrs.color))
    },
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending
})

/**
 * Objects
 */
// Torus knot
const torusKnot = new THREE.Mesh(
    new THREE.TorusKnotGeometry(0.6, 0.25, 128, 32),
    material
)
torusKnot.position.x = 3
scene.add(torusKnot)

// Sphere
const sphere = new THREE.Mesh(
    new THREE.SphereGeometry(),
    material
)
sphere.position.x = -3
scene.add(sphere)

// Suzanne
let suzanne = null
gltfLoader.load(
    './suzanne.glb',
    (gltf) => {
        suzanne = gltf.scene
        suzanne.traverse((child) => {
            if (child.isMesh)
                child.material = material
        })
        scene.add(suzanne)

        finishLoading()
    },
    (progress) => {
        if (progress.total > 0) {
            const percent = (progress.loaded / progress.total) * 100
            updateLoadingProgress(percent)
        }
    }
)

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () => {
    const elapsedTime = clock.getElapsedTime()

    // Smooth mouse
    mouse.x += (mouse.targetX - mouse.x) * 0.05
    mouse.y += (mouse.targetY - mouse.y) * 0.05

    // Update uniforms
    material.uniforms.uTime.value = elapsedTime

    // Rotate objects
    if (suzanne) {
        suzanne.rotation.x = -elapsedTime * 0.1
        suzanne.rotation.y = elapsedTime * 0.2
    }

    sphere.rotation.x = -elapsedTime * 0.1
    sphere.rotation.y = elapsedTime * 0.2

    torusKnot.rotation.x = -elapsedTime * 0.1
    torusKnot.rotation.y = elapsedTime * 0.2

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    window.requestAnimationFrame(tick)
}

tick()
